import React, { useState, useMemo, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useModulePermissions } from '@/hooks/useModulePermissions';
import { useModuleOrder } from '@/hooks/useModuleOrder';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import { RealtimeIndicator } from './RealtimeStatus';
import { ForcedMenuOrder } from './ForcedMenuOrder';
import { UserPresenceIndicator } from '@/components/ui/user-presence-indicator';
import { UserProfileSidebar } from '@/components/ui/user-profile-sidebar';
import { Button } from '@/components/ui/button';
import { Badge as BadgeComponent } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { 
  ChevronDown, 
  ChevronRight,
  LayoutDashboard,
  Users,
  Building2,
  MapPin,
  Package,
  Package2,
  ShoppingCart,
  ShoppingBag,
  Calculator,
  BarChart3,
  Settings,
  Shield,
  Puzzle,
  Settings2,
  Box,
  Tag,
  Warehouse,
  FileText,
  FileEdit,
  UserCheck,
  Cog,
  DollarSign,
  HelpCircle,
  Code,
  FileBarChart,
  RefreshCw,
  AlertTriangle,
  Target,
  TrendingUp,
  CheckSquare,
  Clock,
  Store,
  Wallet,
  // Iconos financieros y de tesorería
  Banknote,
  CreditCard,
  Receipt,
  HandCoins,
  PiggyBank,
  TrendingDown,
  // Iconos de ventas y clientes
  ShoppingBasket,
  Users2,
  UserPlus,
  FileSpreadsheet,
  ClipboardList,
  Receipt as ReceiptIcon,
  Handshake,
  // Iconos de inventario y logística
  Boxes,
  PackageCheck,
  Truck,
  RotateCcw,
  Archive,
  Layers,
  Grid3X3,
  ScanLine,
  // Iconos de compras y proveedores
  ShoppingCart as ShoppingCartIcon,
  Factory,
  FileCheck,
  PackageOpen,
  // Iconos de administración y RRHH
  Crown,
  UserCog,
  Building,
  Badge,
  Calendar,
  ClipboardCheck,
  // Iconos de herramientas y soporte
  Wrench,
  BookOpen,
  MessageCircleQuestion,
  Headphones,
  Settings as SettingsIcon,
  Wand2,
  QrCode,
  Sparkles
} from 'lucide-react';

// Mapeo completo de iconos por categoría
const iconMap: Record<string, React.ComponentType<any>> = {
  LayoutDashboard, Users, Building2, MapPin, Settings, Shield, Puzzle, Settings2, Cog, HelpCircle, Code, RefreshCw, AlertTriangle, Target, TrendingUp, CheckSquare, Clock,
  Wallet, DollarSign, Banknote, CreditCard, Receipt, HandCoins, PiggyBank, TrendingDown, Calculator, BarChart3, FileBarChart,
  ShoppingCart, ShoppingBag, ShoppingBasket, Store, Users2, UserPlus, FileSpreadsheet, ClipboardList, Handshake,
  Package, Package2, Boxes, PackageCheck, PackageOpen, Box, Tag, Warehouse, Truck, RotateCcw, Archive, Layers, Grid3X3, ScanLine, Factory,
  Crown, UserCog, UserCheck, Building, Badge, Calendar, ClipboardCheck,
  FileText, FileEdit, FileCheck, Wrench, BookOpen, MessageCircleQuestion, Headphones, SettingsIcon, Wand2, QrCode, Sparkles
};

interface ExpandedModules {
  [key: string]: boolean;
}

interface AllowedModule {
  module_id: string;
  module_code: string;
  module_name: string;
  module_route: string;
  module_icon: string;
  parent_id: string | null;
  sort_order: number;
}

export function DynamicSidebar() {
  const { open } = useSidebar();
  const location = useLocation();
  const currentPath = location.pathname;
  const { signOut, selectedCompany, user } = useAuth();
  const { allowedModules, loading, refetch } = useModulePermissions();
  const { customGroups, getOrderedModules, refetch: refetchModuleOrder } = useModuleOrder();
  const { isSuperAdmin } = useSuperAdmin();
  const { toast } = useToast();
  
  // Debug logs optimizados - solo cuando hay cambios reales
  const prevDebugInfo = useRef({
    userId: '',
    companyId: '',
    customGroupsLength: 0,
    allowedModulesLength: 0
  });
  
  const currentDebugInfo = {
    userId: user?.id || '',
    companyId: selectedCompany?.id || '',
    customGroupsLength: customGroups.length,
    allowedModulesLength: allowedModules.length
  };
  
  if (JSON.stringify(prevDebugInfo.current) !== JSON.stringify(currentDebugInfo)) {
    logger.log('🔍 [DynamicSidebar] Render started');
    logger.log('🔍 [DynamicSidebar] User:', user?.id);
    logger.log('🔍 [DynamicSidebar] Company:', selectedCompany?.id);
    logger.log('🔍 [DynamicSidebar] Custom groups:', customGroups);
    logger.log('🔍 [DynamicSidebar] Custom groups length:', customGroups.length);
    logger.log('🔍 [DynamicSidebar] Custom groups type:', Array.isArray(customGroups) ? 'array' : typeof customGroups);
    logger.log('🔍 [DynamicSidebar] Allowed modules length:', allowedModules.length);
    prevDebugInfo.current = currentDebugInfo;
  }
  
  const [expandedModules, setExpandedModules] = useState<ExpandedModules>({});

  const handleRefreshModules = async () => {
    logger.log('🔄 [DynamicSidebar] Refreshing modules and module order...');
    toast({
      title: "Actualizando menú",
      description: "Recargando módulos disponibles..."
    });
    
    try {
      await Promise.all([refetch(), refetchModuleOrder()]);
      
      // Forzar recarga completa para actualizar autenticación
      setTimeout(() => {
        window.location.reload();
      }, 500);
      
    } catch (error) {
      logger.error('Error refreshing modules:', error);
      toast({
        title: "Error",
        description: "No se pudieron actualizar los módulos",
        variant: "destructive"
      });
    }
  };

  const mainModules = useMemo(() => 
    allowedModules.filter(module => !module.parent_id),
    [allowedModules]
  );

  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const getIcon = (iconName: string | null, moduleName?: string, moduleCode?: string) => {
    if (iconName && iconMap[iconName]) {
      return iconMap[iconName];
    }

    if (moduleName) {
      const name = moduleName.toUpperCase();
      if (name.includes('VENTAS') || name.includes('SALES')) return ShoppingCart;
      if (name.includes('POS') || name.includes('PUNTO DE VENTA')) return Store;
      if (name.includes('COTIZACIONES') || name.includes('QUOTES')) return FileText;
      if (name.includes('CLIENTES') || name.includes('CUSTOMERS')) return Users;
      if (name.includes('CAJA') || name.includes('CASH')) return HandCoins;
      if (name.includes('INVENTARIO') || name.includes('INVENTORY')) return Warehouse;
      if (name.includes('REPORTES') || name.includes('REPORTS')) return BarChart3;
      if (name.includes('CONFIGURACION') || name.includes('CONFIG')) return Settings;
    }

    return LayoutDashboard;
  };

  const isActive = (path: string | null) => {
    if (!path) return false;
    return currentPath === path || currentPath.startsWith(path + '/');
  };

  const getNavClasses = (isActiveRoute: boolean) => 
    isActiveRoute 
      ? "bg-primary text-primary-foreground font-medium" 
      : "hover:bg-accent text-foreground";

  const renderModuleItem = (module: AllowedModule) => {
    const subModules = allowedModules.filter(m => m.parent_id === module.module_id);

    // Fallback: garantizar que "Marcas" exista en Inventario aunque no esté en permisos aún
    if ((module.module_name || '').toUpperCase().includes('INVENTARIO')) {
      const hasBrands = subModules.some(m => m.module_route === '/inventory/brands');
      if (!hasBrands) {
        const brandsModule: AllowedModule = {
          module_id: 'inventory-brands',
          module_code: 'inventory-brands',
          module_name: 'Marcas',
          module_route: '/inventory/brands',
          module_icon: 'Tag',
          parent_id: module.module_id,
          sort_order: 0,
        };
        const idxCat = subModules.findIndex(m => m.module_route === '/inventory/categories');
        if (idxCat >= 0) subModules.splice(idxCat + 1, 0, brandsModule);
        else subModules.unshift(brandsModule);
      }

      // Fallback: garantizar que "Generación Masiva" exista en Inventario aunque no esté en permisos
      const hasMassGen = subModules.some(m => m.module_route === '/inventory/mass-generation');
      if (!hasMassGen) {
        const massGenModule: AllowedModule = {
          module_id: 'inventory-mass-generation',
          module_code: 'inventory-mass-generation',
          module_name: 'Generación Masiva',
          module_route: '/inventory/mass-generation',
          module_icon: 'Wand2',
          parent_id: module.module_id,
          sort_order: 0,
        };
        // Insertar después de Marcas si existe, o al final
        const idxBrands = subModules.findIndex(m => m.module_route === '/inventory/brands');
        if (idxBrands >= 0) subModules.splice(idxBrands + 1, 0, massGenModule);
        else subModules.push(massGenModule);
      }

      // Fallback: garantizar que "Generador de Códigos" exista en Inventario
      const hasCodeGen = subModules.some(m => m.module_route === '/inventory/code-generator');
      if (!hasCodeGen) {
        const codeGenModule: AllowedModule = {
          module_id: 'inventory-code-generator',
          module_code: 'inventory-code-generator',
          module_name: 'Generador de Códigos',
          module_route: '/inventory/code-generator',
          module_icon: 'QrCode',
          parent_id: module.module_id,
          sort_order: 0,
        };
        // Insertar después de Generación Masiva
        const idxMassGen = subModules.findIndex(m => m.module_route === '/inventory/mass-generation');
        if (idxMassGen >= 0) subModules.splice(idxMassGen + 1, 0, codeGenModule);
        else subModules.push(codeGenModule);
      }

      // Fallback: garantizar que "Textos Predefinidos" exista en Inventario
      const hasPredefinedTexts = subModules.some(m => m.module_route === '/inventory/predefined-texts');
      if (!hasPredefinedTexts) {
        const predefinedTextsModule: AllowedModule = {
          module_id: 'inventory-predefined-texts',
          module_code: 'inventory-predefined-texts',
          module_name: 'Textos Predefinidos',
          module_route: '/inventory/predefined-texts',
          module_icon: 'FileText',
          parent_id: module.module_id,
          sort_order: 0,
        };
        // Insertar después de Generador de Códigos
        const idxCodeGen = subModules.findIndex(m => m.module_route === '/inventory/code-generator');
        if (idxCodeGen >= 0) subModules.splice(idxCodeGen + 1, 0, predefinedTextsModule);
        else subModules.push(predefinedTextsModule);
      }

      // Fallback: garantizar que "Mapeo de Precios" exista en Inventario
      const hasPriceMapping = subModules.some(m => m.module_route === '/inventory/price-mapping');
      if (!hasPriceMapping) {
        const priceMappingModule: AllowedModule = {
          module_id: 'inventory-price-mapping',
          module_code: 'inventory-price-mapping',
          module_name: 'Mapeo de Precios',
          module_route: '/inventory/price-mapping',
          module_icon: 'DollarSign',
          parent_id: module.module_id,
          sort_order: 0,
        };
        // Insertar después de Listas de Precios si existe, o después de Textos Predefinidos
        const idxPriceLists = subModules.findIndex(m => m.module_route === '/inventory/price-lists');
        if (idxPriceLists >= 0) subModules.splice(idxPriceLists + 1, 0, priceMappingModule);
        else {
          const idxTexts = subModules.findIndex(m => m.module_route === '/inventory/predefined-texts');
          if (idxTexts >= 0) subModules.splice(idxTexts + 1, 0, priceMappingModule);
          else subModules.push(priceMappingModule);
        }
      }
    }

    // Fallback: garantizar que "Conciliación Digifact" y "Gestión de Cierres de Caja" existan en Ventas aunque no estén en permisos
    if ((module.module_name || '').toUpperCase().includes('VENTAS') || 
        (module.module_name || '').toUpperCase().includes('SALES') ||
        (module.module_code || '').toLowerCase().includes('sales')) {
      const hasDigifact = subModules.some(m => m.module_route === '/sales/digifact-diagnostic');
      if (!hasDigifact) {
        const digifactModule: AllowedModule = {
          module_id: 'sales-digifact-diagnostic',
          module_code: 'sales-digifact-diagnostic',
          module_name: 'Conciliación Digifact',
          module_route: '/sales/digifact-diagnostic',
          module_icon: 'FileCheck',
          parent_id: module.module_id,
          sort_order: 999,
        };
        subModules.push(digifactModule);
      }

      const hasClosureTracking = subModules.some(m => m.module_route === '/sales/closure-tracking');
      if (!hasClosureTracking) {
        const closureTrackingModule: AllowedModule = {
          module_id: 'sales-closure-tracking',
          module_code: 'sales-closure-tracking',
          module_name: 'Gestión de Cierres de Caja',
          module_route: '/sales/closure-tracking',
          module_icon: 'FileCheck',
          parent_id: module.module_id,
          sort_order: 120,
        };
        // Insertar cerca de Gestión de Caja si existe
        const idxCashMgmt = subModules.findIndex(m => m.module_route === '/sales/cash-management');
        if (idxCashMgmt >= 0) subModules.splice(idxCashMgmt + 1, 0, closureTrackingModule);
        else subModules.push(closureTrackingModule);
      }
    }

    const hasSubModules = subModules.length > 0;
    const isExpanded = expandedModules[module.module_id];
    const isActiveRoute = isActive(module.module_route);
    const Icon = getIcon(module.module_icon, module.module_name, module.module_code);

    return (
      <div key={module.module_id}>
        <SidebarMenuItem>
          {hasSubModules ? (
            <div className="flex items-center w-full mx-2">
              <SidebarMenuButton asChild className="flex-1">
                <NavLink 
                  to={module.module_route || '#'} 
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors duration-200 ${getNavClasses(isActiveRoute)}`}
                >
                  <div className={`h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isActiveRoute 
                      ? 'bg-primary-foreground/20' 
                      : 'bg-primary/10'
                  }`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  {open && <span className="text-sm font-medium">{module.module_name}</span>}
                </NavLink>
              </SidebarMenuButton>
              {open && (
                <button
                  onClick={() => toggleModule(module.module_id)}
                  className="p-1.5 hover:bg-accent rounded-lg transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                </button>
              )}
            </div>
          ) : (
            <SidebarMenuButton asChild>
              <NavLink 
                to={module.module_route || '#'} 
                className={`flex items-center gap-3 mx-2 px-3 py-2.5 rounded-xl transition-colors duration-200 ${getNavClasses(isActiveRoute)}`}
              >
                <div className={`h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isActiveRoute 
                    ? 'bg-primary-foreground/20' 
                    : 'bg-primary/10'
                }`}>
                  <Icon className="h-4 w-4" />
                </div>
                {open && <span className="text-sm font-medium">{module.module_name}</span>}
              </NavLink>
            </SidebarMenuButton>
          )}
        </SidebarMenuItem>

        {hasSubModules && open && isExpanded && (
          <div className="ml-6 space-y-1 border-l border-border/30 pl-3 py-2">
            {subModules.map(subModule => {
              const SubIcon = getIcon(subModule.module_icon, subModule.module_name, subModule.module_code);
              const isSubActive = isActive(subModule.module_route);
              return (
                <SidebarMenuItem key={subModule.module_id}>
                  <SidebarMenuButton asChild>
                    <NavLink 
                      to={subModule.module_route || '#'} 
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors duration-200 text-sm ${
                        isSubActive 
                          ? 'bg-primary/10 text-primary font-medium' 
                          : 'hover:bg-accent text-muted-foreground'
                      }`}
                    >
                      <SubIcon className="h-3.5 w-3.5" />
                      <span className="font-medium">{subModule.module_name}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Sidebar collapsible="icon">
        <SidebarTrigger className="m-2 self-end" />
        <SidebarContent>
          <div className="p-4 text-center text-muted-foreground">
            Cargando...
          </div>
        </SidebarContent>
      </Sidebar>
    );
  }

  return (
    <Sidebar 
      collapsible="icon" 
      variant="floating"
      className="bg-card/95 backdrop-blur-sm border border-border/20 rounded-2xl"
    >
      <SidebarTrigger className="m-3 self-end h-8 w-8 rounded-lg hover:bg-accent transition-colors duration-200" />
      
      <SidebarContent className="bg-transparent">
        {/* Dashboard siempre visible */}
        <SidebarGroup className="border-b border-border/10 pb-4">
          <SidebarGroupLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-3 mb-2">
            Vista General
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink 
                    to="/" 
                    className={`flex items-center gap-3 px-3 py-2.5 mx-2 rounded-xl transition-colors duration-200 ${
                      isActive('/') || currentPath === '/dashboard'
                        ? 'bg-primary text-primary-foreground font-medium' 
                        : 'hover:bg-accent text-foreground'
                    }`}
                  >
                    <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${
                      isActive('/') || currentPath === '/dashboard'
                        ? 'bg-primary-foreground/20' 
                        : 'bg-primary/10'
                    }`}>
                      <LayoutDashboard className="h-4 w-4" />
                    </div>
                    {open && <span className="text-sm font-medium">Dashboard</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* CRM - Siempre visible para usuarios con permiso de ventas */}
        <SidebarGroup className="border-b border-border/5" style={{ padding: 'var(--sidebar-padding)' }}>
          <SidebarGroupLabel className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-[0.15em] flex items-center gap-2" style={{ marginBottom: 'var(--spacing-md)', paddingLeft: 'var(--spacing-lg)', paddingRight: 'var(--spacing-lg)' }}>
            <Handshake className="h-3.5 w-3.5 text-primary/70" />
            CRM
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu style={{ gap: 'var(--sidebar-item-padding)' }}>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink 
                    to="/crm" 
                    className={`flex items-center gap-3 px-3 py-2.5 mx-2 rounded-xl transition-colors duration-200 ${
                      isActive('/crm')
                        ? 'bg-primary text-primary-foreground font-medium' 
                        : 'hover:bg-accent text-foreground'
                    }`}
                  >
                    <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${
                      isActive('/crm')
                        ? 'bg-primary-foreground/20' 
                        : 'bg-primary/10'
                    }`}>
                      <Users2 className="h-4 w-4" />
                    </div>
                    {open && <span className="text-sm font-medium">Pipeline de Ventas</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink 
                    to="/corporate-contacts" 
                    className={`flex items-center gap-3 px-3 py-2.5 mx-2 rounded-xl transition-colors duration-200 ${
                      isActive('/corporate-contacts')
                        ? 'bg-primary text-primary-foreground font-medium' 
                        : 'hover:bg-accent text-foreground'
                    }`}
                  >
                    <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${
                      isActive('/corporate-contacts')
                        ? 'bg-primary-foreground/20' 
                        : 'bg-primary/10'
                    }`}>
                      <Building2 className="h-4 w-4" />
                    </div>
                    {open && <span className="text-sm font-medium">Contactos Corporativos</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Menú homologado (estilo simple como Demo 2): una sola sección "OTROS" para todos */}
        {(() => {
          const keyOf = (m: AllowedModule) => m.module_id || m.module_route || m.module_code || m.module_name;

          // Tomar solo módulos principales (sin padre) y excluir Dashboard y CRM
          const main = allowedModules.filter(m => !m.parent_id);
          const seen = new Set<string>();

          const otherModules = main.filter(m => {
            if (m.module_route === '/' || m.module_route === '/dashboard' || m.module_route === '/crm') return false;
            const k = keyOf(m);
            if (k && seen.has(k)) return false;
            if (k) seen.add(k);
            return true;
          }).sort((a, b) => ((a.sort_order ?? 9999) - (b.sort_order ?? 9999)) || a.module_name.localeCompare(b.module_name, 'es'));

          if (otherModules.length === 0) return null;

          return (
            <SidebarGroup className="border-b border-border/5" style={{ padding: 'var(--sidebar-padding)' }}>
              <SidebarGroupLabel className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-[0.15em] flex items-center gap-2" style={{ marginBottom: 'var(--spacing-md)', paddingLeft: 'var(--spacing-lg)', paddingRight: 'var(--spacing-lg)' }}>
                <Puzzle className="h-3.5 w-3.5 text-primary/70" />
                Otros
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu style={{ gap: 'var(--sidebar-item-padding)' }}>
                  {otherModules.map(module => renderModuleItem(module))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })()}


        {/* Herramientas siempre visible */}
        <SidebarGroup className="border-b border-border/5" style={{ padding: 'var(--sidebar-padding)' }}>
          <SidebarGroupLabel className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-[0.15em] flex items-center gap-2" style={{ marginBottom: 'var(--spacing-md)', paddingLeft: 'var(--spacing-lg)', paddingRight: 'var(--spacing-lg)' }}>
            <Wrench className="h-3.5 w-3.5 text-primary/70" />
            Herramientas y Soporte
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu style={{ gap: 'var(--sidebar-item-padding)' }}>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink 
                    to="/implementation" 
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 ${getNavClasses(isActive('/implementation'))}`}
                  >
                    <Code className="h-4 w-4 flex-shrink-0" />
                    {open && <span className="truncate">Implementación</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink 
                    to="/user-manual" 
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 ${getNavClasses(isActive('/user-manual'))}`}
                  >
                    <FileText className="h-4 w-4 flex-shrink-0" />
                    {open && <span className="truncate">Manual de Usuario</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Servicio Técnico */}
              {(isSuperAdmin || allowedModules.some(m => m.module_code === 'service_tech' || m.module_route === '/service-tech')) && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink 
                      to="/service-tech" 
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 ${getNavClasses(isActive('/service-tech'))}`}
                    >
                      <Wrench className="h-4 w-4 flex-shrink-0 text-primary" />
                      {open && <span className="truncate">Servicio Técnico</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {/* Soporte SaaS - Disponible para todos los usuarios */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink 
                    to="/support-tickets" 
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 ${getNavClasses(isActive('/support-tickets'))}`}
                  >
                    <HelpCircle className="h-4 w-4 flex-shrink-0 text-status-info" />
                    {open && <span className="truncate">Soporte SaaS</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              
              {/* Gestión de Casos de Clientes - Sistema interno */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink 
                    to="/customer-cases" 
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 ${getNavClasses(isActive('/customer-cases'))}`}
                  >
                    <MessageCircleQuestion className="h-4 w-4 flex-shrink-0 text-status-success" />
                    {open && <span className="truncate">Gestión de Casos</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Generador Visual IA - Productos */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink 
                    to="/admin/product-visual-generator" 
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 ${getNavClasses(isActive('/admin/product-visual-generator'))}`}
                  >
                    <Sparkles className="h-4 w-4 flex-shrink-0 text-primary" />
                    {open && <span className="truncate">Generador Visual IA</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Configuración Inter-Company - Solo Super Admin */}
              {isSuperAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink 
                      to="/admin/inter-company" 
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 ${getNavClasses(isActive('/admin/inter-company'))}`}
                    >
                      <Shield className="h-4 w-4 flex-shrink-0 text-warning" />
                      {open && (
                        <div className="flex items-center gap-2 truncate">
                          <span className="truncate">Inter-Company</span>
                          <BadgeComponent variant="secondary" className="text-xs px-1.5 py-0.5">
                            Admin
                          </BadgeComponent>
                        </div>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {/* SaaS Admin - Solo Super Admin */}
              {isSuperAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink 
                      to="/saas-admin" 
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 ${getNavClasses(isActive('/saas-admin'))}`}
                    >
                      <Crown className="h-4 w-4 flex-shrink-0 text-primary" />
                      {open && (
                        <div className="flex items-center gap-2 truncate">
                          <span className="truncate">SaaS Admin</span>
                          <BadgeComponent variant="default" className="text-xs px-1.5 py-0.5 bg-primary text-primary-foreground">
                            SaaS
                          </BadgeComponent>
                        </div>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* User Profile y Status */}
        <div className="mt-auto">
          <div className="p-3 border-t border-border/20">
            <UserPresenceIndicator />
          </div>
          
          {/* User Profile */}
          <UserProfileSidebar />
        </div>
      </SidebarContent>
    </Sidebar>
  );
}
